/**
 * 인앱 값 선택기 (노션식 선택/다중 선택/담당자 픽커).
 * openPillPicker(anchor, {options:[{label,color}], values:[], multi, allowNew,
 *   neutral, placeholder, newLabel, onChange(values), onNew(label)})
 * 검색/새 옵션 입력줄 + 옵션 목록. 단일 선택은 고르면 닫히고, 다중은 토글.
 * Enter 로 입력한 값이 없으면 새 옵션으로 만든다 (allowNew).
 */
let openEl = null;
let restoreFocus = null; // 피커를 연 요소 — 닫힐 때 포커스를 돌려준다 (키보드 흐름, QA ux-5)

export function closePillPicker() {
    if (openEl) {
        const hadFocus = openEl.contains(document.activeElement);
        openEl.remove();
        openEl = null;
        if (hadFocus && restoreFocus && restoreFocus.isConnected && restoreFocus.focus) restoreFocus.focus({ preventScroll: true });
    }
    restoreFocus = null;
}

export const OPTION_COLORS = ['o1', 'o2', 'o3', 'o4', 'o5', 'o6', 'o7', 'o8'];

export function pill(label, color, neutral) {
    const s = document.createElement('span');
    s.className = 'prop-pill ' + (neutral ? 'neutral' : (color || 'o1'));
    s.textContent = label;
    return s;
}

export function openPillPicker(anchor, opts) {
    closePillPicker();
    const o = opts || {};
    let values = (o.values || []).slice();
    const options = (o.options || []).slice();
    const pop = document.createElement('div');
    pop.className = 'prop-pop';
    pop.setAttribute('role', 'listbox');
    const head = document.createElement('div');
    head.className = 'prop-pop-head';
    const chips = document.createElement('div');
    chips.className = 'prop-pop-chips';
    const input = document.createElement('input');
    input.type = 'text';
    input.placeholder = o.placeholder || '';
    input.autocomplete = 'off';
    head.appendChild(chips);
    head.appendChild(input);
    const list = document.createElement('div');
    list.className = 'prop-pop-list';
    pop.appendChild(head);
    pop.appendChild(list);

    function emit() {
        if (o.onChange) o.onChange(values.slice());
    }
    function renderChips() {
        chips.innerHTML = '';
        values.forEach((v) => {
            const opt = options.find((x) => x.label === v);
            const p = pill(opt ? opt.label : v, opt ? opt.color : 'o8', o.neutral);
            const rm = document.createElement('button');
            rm.type = 'button';
            rm.textContent = '✕';
            rm.addEventListener('click', (e) => { e.stopPropagation(); values = values.filter((x) => x !== v); renderChips(); renderList(); emit(); });
            p.appendChild(rm);
            chips.appendChild(p);
        });
    }
    function choose(label) {
        if (o.multi) {
            if (values.includes(label)) values = values.filter((x) => x !== label);
            else values.push(label);
            renderChips();
            renderList();
            emit();
        } else {
            values = [label];
            emit();
            closePillPicker();
        }
    }
    // 키보드 커서 (↑↓ Home End, Enter 로 선택 — 사람 피커와 같은 흐름, QA ux-3)
    let cursor = -1;
    const items = () => Array.from(list.querySelectorAll('.prop-pop-item'));
    function setCursor(i) {
        const its = items();
        if (!its.length) { cursor = -1; return; }
        cursor = ((i % its.length) + its.length) % its.length;
        its.forEach((b, k) => b.classList.toggle('cur', k === cursor));
        if (its[cursor].scrollIntoView) its[cursor].scrollIntoView({ block: 'nearest' });
    }
    function renderList() {
        list.innerHTML = '';
        const q = input.value.trim().toLowerCase();
        let shown = 0;
        if (!o.multi && values.length && !o.required) {
            const none = document.createElement('button');
            none.type = 'button';
            none.className = 'prop-pop-item none';
            none.textContent = o.noneLabel || '— 없음';
            none.addEventListener('click', () => { values = []; emit(); closePillPicker(); });
            list.appendChild(none);
        }
        options.forEach((opt) => {
            if (q && !opt.label.toLowerCase().includes(q)) return;
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'prop-pop-item' + (values.includes(opt.label) ? ' sel' : '');
            b.appendChild(pill(opt.label, opt.color, o.neutral));
            if (values.includes(opt.label)) {
                const ck = document.createElement('span');
                ck.className = 'prop-pop-check';
                ck.textContent = '✓';
                b.appendChild(ck);
            }
            b.addEventListener('click', () => choose(opt.label));
            list.appendChild(b);
            shown += 1;
        });
        if (o.allowNew && q && !options.some((x) => x.label.toLowerCase() === q)) {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'prop-pop-item new';
            b.textContent = (o.newLabel || '새 옵션') + ': ' + input.value.trim();
            b.addEventListener('click', () => addNew(input.value.trim()));
            list.appendChild(b);
            shown += 1;
        }
        if (!shown) {
            const e = document.createElement('div');
            e.className = 'prop-pop-empty';
            e.textContent = o.emptyLabel || '—';
            list.appendChild(e);
        }
        // 검색 중이면 첫 항목에, 아니면 현재 값(없으면 첫 옵션)에 커서
        const its = items();
        const selIdx = its.findIndex((b) => b.classList.contains('sel'));
        cursor = -1;
        if (its.length) setCursor(q ? 0 : (selIdx >= 0 ? selIdx : (its[0].classList.contains('none') && its.length > 1 ? 1 : 0)));
    }
    function addNew(label) {
        if (!label) return;
        const color = OPTION_COLORS[options.length % OPTION_COLORS.length];
        options.push({ label, color });
        if (o.onNew) o.onNew({ label, color });
        input.value = '';
        choose(label);
    }
    input.addEventListener('input', renderList);
    input.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(cursor + 1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(cursor - 1); }
        else if (e.key === 'Home') { e.preventDefault(); setCursor(0); }
        else if (e.key === 'End') { e.preventDefault(); setCursor(items().length - 1); }
        else if (e.key === 'Tab') { closePillPicker(); }
        else if (e.key === 'Enter') {
            e.preventDefault();
            const its = items();
            const hit = its[cursor] || its[0];
            if (hit) { hit.click(); return; }
            const q = input.value.trim();
            const exact = options.find((x) => x.label.toLowerCase() === q.toLowerCase());
            if (exact) choose(exact.label);
            else if (q && o.allowNew) addNew(q);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            closePillPicker();
        } else if (e.key === 'Backspace' && !input.value && o.multi && values.length) {
            values.pop();
            renderChips();
            renderList();
            emit();
        }
    });
    if (o.multi) renderChips();
    else chips.hidden = true;
    renderList();
    document.body.appendChild(pop);
    const r = anchor.getBoundingClientRect();
    const w = Math.max(220, r.width);
    pop.style.left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8)) + 'px';
    pop.style.top = (r.bottom + 4 + pop.offsetHeight > window.innerHeight ? Math.max(8, r.top - pop.offsetHeight - 4) : r.bottom + 4) + 'px';
    pop.style.minWidth = w + 'px';
    openEl = pop;
    restoreFocus = document.activeElement && document.activeElement !== document.body ? document.activeElement : null;
    input.focus();
    return { close: closePillPicker };
}

document.addEventListener('mousedown', (e) => { if (openEl && !openEl.contains(e.target)) closePillPicker(); }, true);
document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && openEl) { closePillPicker(); e.stopImmediatePropagation(); } }, true);
